import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import {
  LiliRadioChipGroup,
  RadioItemStyle,
} from '../../../../generic-components/LiliRadioChipGroup/LiliRadioChipGroup';
import { achOutTransferContentMap } from '../../consts';
import { LiliTooltip, LiliToolTipColor, TooltipPosition } from '../../../../DesignSystem/LiliTooltip/LiliTooltip';
import { convertValueToUSD } from '../../../../helpers/currency.helper';
import { TRANSFER_TYPES } from '../../steps/Review';
import { useMoveMoneyLogic } from '../../hooks/useMoveMoneyLogic';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/svg/shared/ic-shared-info.svg';
import { IAchOutFee } from '../../api/getAchOutFees';

interface IAchOutFeeSelectorProps {
  achOutTransferType: TRANSFER_TYPES;
  achOutActiveFeeData: IAchOutFee | undefined;
  setAchOutTransferType: React.Dispatch<React.SetStateAction<TRANSFER_TYPES>>;
}

export const AchOutFeeSelector = forwardRef<HTMLDivElement, IAchOutFeeSelectorProps>((props, ref) => {
  const { achOutTransferType, setAchOutTransferType, achOutActiveFeeData } = props;
  const { moveMoneyData } = useMoveMoneyLogic();
  const { t } = useTranslation();

  return moveMoneyData.achOutFeeData ? (
    <>
      <div
        ref={ref}
        className='flex flex-col py-[16px] px-[16px] rounded-[8px] border border-bw03 w-[432px] mx-auto mb-[8px] relative'
      >
        <Heading title='Choose transfer time' className='font-metricR text-bw07 text-[16px] mb-[12px]' />
        <LiliRadioChipGroup
          items={moveMoneyData.achOutFeeData.fees.map((feeItem) => ({
            checked: feeItem.achTransferType === achOutTransferType,
            radioGroupId: 'TransferTime',
            value: feeItem.achTransferType,
            label: achOutTransferContentMap[feeItem.achTransferType].title,
            icon: achOutTransferContentMap[feeItem.achTransferType].icon,
            description: feeItem.arrivalDateShort,
          }))}
          isVertical
          onChange={(value) => setAchOutTransferType(value as TRANSFER_TYPES)}
          groupId='TransferTime'
          radioItemStyle={RadioItemStyle.CHIP}
        />
        <div className='flex items-center justify-between mt-[16px]'>
          <div className='flex items-center gap-[6px]'>
            <Heading title='Fee' className='font-metricR text-bw07 text-[16px]' />
            <LiliTooltip
              position={TooltipPosition.TOP_START}
              title={t('MOVE_MONEY.STEPS.REVIEW.ACH_OUT.FEE_TOOLTIP_TITLE')}
              description={t('MOVE_MONEY.STEPS.REVIEW.ACH_OUT.FEE_TOOLTIP_DESCRIPTION')}
              targetId='ach-out-fee-tool-tip'
              color={LiliToolTipColor.White}
              width='315px'
            />
            <InfoIcon data-tooltip-id='ach-out-fee-tool-tip' className='cursor-pointer' />
          </div>
          <Heading
            id='feeValue'
            title={convertValueToUSD(achOutActiveFeeData?.fee || 0, 2)}
            className='font-metricR text-bw08 text-[16px]'
          />
        </div>
      </div>
      <div className='flex justify-center mx-auto w-[432px]'>
        <p data-testid='SendMoney-review-disclaimer' className='font-metricR text-sm text-bw06 leading-5'>
          {t('SEND_MONEY.REVIEW.TRANSFER_DISCLAIMER')}
        </p>
      </div>
    </>
  ) : null;
});
